import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownToLine, ArrowRightToLine, Check, Clock3, X } from "lucide-react";
import { AiOutlineExclamation } from "react-icons/ai";
import CustomTable from '@/shared/components/CustomTable'
import { formatTimestamp, upperCase } from "@/shared/lib/appHelpers"
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"

function ReportsTable({ 
  selectedTabReports, 
  hideTypeColumn, 
  handleReportDrawer, 
  handleRetry 
}) {
  const { t } = useTranslation();
  const { currentToolFields, toolName } = useCurrentTool();
  const toolFieldHeaders = currentToolFields.map((field) => ({
    Header: t(`fields.${field.name}`),
    accessor: field.name,
  }));

  const typeHeader = hideTypeColumn.includes(toolName) ? [] : [{
    Header: t("reports.table.type"),
    accessor: "template_name",
    Cell: ({ value, report }) => {
      const displayText = report.uses_custom_template ? value : value === 'select_template' ? '-' : t(`reports.table.types.${upperCase(value)}`);
      const truncatedText = String(displayText).length > 20 ? String(displayText).substring(0, 20) + '...' : displayText;
      return <span>{truncatedText}</span>
    }
  }];

  const columns = [
    {
      Header: "ID",
      accessor: "row_number",
    },
    {
      Header: t("reports.table.status"),
      accessor: "status",
      Cell: ({ value }) => {
        const statusIcons = {
          "generated": <ArrowDownToLine className="text-[#F17B2C] h-4 w-4"/>,
          "failed": <X className="text-[#EF4343] h-4 w-4"/>,
          "completed": <Check className="text-[#38C793] h-4 w-4"/>,
          "in-progress": <ArrowRightToLine className="text-blue-500 h-4 w-4"/>,
          "urgent": <AiOutlineExclamation className="text-white h-4 w-4"/>,
          "processing": <Clock3 className="text-[#868C98] h-4 w-4"/>
        };
    
        const statusToTranslationKey = {
          "generated": "reports.tabs.generated",
          "failed": "reports.tabs.failed", 
          "completed": "reports.tabs.completed",
          "in-progress": "reports.tabs.inProgress",
          "urgent": "reports.tabs.urgent",
          "processing": "reports.tabs.processing"
        };
    
        const translationKey = statusToTranslationKey[value] || value;
    
        return (
          <div className={`border border-[#e3e4e9] flex justify-center gap-1 w-fit cursor-pointer ${value === 'urgent' ? 'bg-red-500' : 'bg-white'} rounded-md p-1 pr-2 ${value === "failed" && 'cursor-not-allowed'}`}>
            {statusIcons[value]}
            <span className={`font-SuisseIntlLight font-semibold text-xs ${value === 'urgent' ? 'text-white' : 'text-[#525866]'}`}>
              {t(translationKey)}
            </span>
          </div>
        );
      },
    },
    ...toolFieldHeaders,
    ...typeHeader,
    {
      Header: t("reports.table.date"),
      accessor: "created_at",
      Cell: ({ value }) => formatTimestamp(value, "m/d/y/h"),
    },
    {
      Header: t("reports.table.user"),
      accessor: "user_name",
    },
  ];

  return (
    <CustomTable
      selectedTabReports={selectedTabReports?.reports}
      columns={columns}
      onClickFunction={handleReportDrawer}
      handleRetry={handleRetry}
    />
  );
}

export default ReportsTable;
